import React, { useEffect, useMemo, useState } from "react";
import { FaStore, FaUserTie, FaPaperPlane, FaHandHoldingUsd } from "react-icons/fa";
import useAuth from "../../../Hooks/useAuth";
import usePermission from "../../../Hooks/usePermission";
import usePersistedFilters from "../../../Hooks/usePersistedFilters";
import { PageShell } from "../../../Components/shared";
import {
  useLazyGetDealerRolesFirmsQuery,
  useGetDealerSendableSubFirmQuotaQuery,
  useSendDealerSubFirmQuotaMutation,
} from "../../../store/dealerRoles/firmApi";
import { useAlert } from "../../../context/AlertContext";
import useFirmBusy from "../../../Hooks/dealerRoles/useFirmBusy";
import QuotaSplitCards from "../../../Components/Firm/Tabs/QuotaSplitCards";
import SubFirmQuotaDrawer from "../../../Components/Firm/Tabs/SubFirmQuotaDrawer";
import { useDebounce } from "../../../Hooks/useDebounce";
import FirmHeader from "../../../Components/Firm/dealerRoles/FirmHeader";
import FirmList from "../../../Components/Firm/dealerRoles/FirmList";
import { ALL_DEALER_FIRM_TAB_PERMISSIONS } from "../../../utils/firmUtils";

// variant'a göre sayfa başlığı/ikonu. Menüde "Firmalar" bayi için
// "Müşterilerim" ve "Bayilerim" olarak ikiye ayrılır.
const VARIANT_META = {
  dealers: {
    title: "Bayilerim",
    subtitle: "Ana bayi ve bağlı bayilerinizi yönetin",
    icon: FaStore,
    firmLabel: "Bayi",
  },
  customers: {
    title: "Müşterilerim",
    subtitle: "Bağlı müşteri firmalarınızı yönetin",
    icon: FaUserTie,
    firmLabel: "Müşteri",
  },
};

/**
 * Müşterilerim satırı → SubFirmQuotaDrawer'ın beklediği biçim: ürün başına
 * havuz (`poolQuota`, firma ProductFirm kotası) + cihazlardaki (`quota`).
 * Liste ucu bunları `quotaArray` olarak döner (firmQuota / deviceQuota).
 */
const toTransferFirm = (firm) => {
  const deviceByName = new Map(
    (firm.quotaArray || []).map((q) => [q.name, Number(q.deviceQuota) || 0])
  );
  const raw = Array.isArray(firm.productInfo?.productInfo)
    ? firm.productInfo.productInfo
    : [];
  return {
    ...firm,
    productInfo: {
      ...firm.productInfo,
      productInfo: raw.map((p) => ({
        ...p,
        poolQuota: Number(p.quota) || 0,
        quota: deviceByName.get(p.productName) || 0,
      })),
    },
  };
};

const toQuotaSummary = (firm) =>
  (firm?.productInfo?.productInfo || [])
    .filter((p) => p.quotaDistribution !== false)
    .map((p) => ({
      productName: p.productName,
      available: p.poolQuota,
      deviceQuota: p.quota,
      total: p.poolQuota + p.quota,
    }));

function DealerFirmPage({ variant = "dealers" }) {
  const { auth } = useAuth();
  const { hasAnyPermission, hasPermission } = usePermission();
  const canNavigateToFirm = hasAnyPermission(...ALL_DEALER_FIRM_TAB_PERMISSIONS);
  // Filtreler sessionStorage'da (varyant başına ayrı anahtar): detaydan geri
  // dönünce aynı aramayla liste açılır.
  const [firmFilters, setFirmFilters] = usePersistedFilters(
    `firmList:dealer:${variant}:filters`,
    { firmName: "", bayserNo: "" },
  );
  const { firmName: firmNameSearch, bayserNo: bayserNoSearch } = firmFilters;
  const makeFilterSetter = (key) => (next) =>
    setFirmFilters((prev) => ({
      ...prev,
      [key]: typeof next === "function" ? next(prev[key]) : next,
    }));
  const setFirmNameSearch = makeFilterSetter("firmName");
  const setBayserNoSearch = makeFilterSetter("bayserNo");
  // Arama 500ms debounce: yazmayı bıraktıktan sonra istek atılır
  const debouncedFirmName = useDebounce(firmNameSearch, 500);
  const debouncedBayserNo = useDebounce(bayserNoSearch, 500);

  const meta = VARIANT_META[variant] || VARIANT_META.dealers;

  const [
    getDealerFirms,
    { data: firmData, isLoading, isFetching },
  ] = useLazyGetDealerRolesFirmsQuery();

  const loadFirms = () =>
    getDealerFirms({
      token: auth.accessToken,
      firmName: debouncedFirmName,
      bayserNo: debouncedBayserNo,
    });

  useEffect(() => {
    if (auth.accessToken) loadFirms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.accessToken, getDealerFirms, debouncedFirmName, debouncedBayserNo]);

  const allFirms = useMemo(() => firmData?.data?.firms || [], [firmData]);

  // Her görünüm yalnızca kendi kapsamındaki firmaları sayar (Bayiler: ana
  // bayi + bayi; Müşteriler: müşteri). Böylece başlıktaki toplam/aktif
  // sayaçları o sayfaya ait olur.
  const scopedFirms = useMemo(() => {
    const relations =
      variant === "customers" ? ["musteri"] : ["anaBayi", "bayi"];
    return allFirms.filter((f) => relations.includes(f?.dealerRelation));
  }, [allFirms, variant]);

  const myDealerFirm = useMemo(
    () => allFirms.find((f) => f.dealerRelation === "anaBayi" || f.dealerRelation === "bayi"),
    [allFirms]
  );

  // ─── Müşterilerim: kota başlığı + Kota Ver / Al (#303) ────────────────────
  const isCustomers = variant === "customers";
  const ownFirmId = String(auth?.firm?._id || auth?.firm || myDealerFirm?._id || "");
  const canSendQuota = isCustomers && hasPermission("customers.sendQuota");
  const { showSuccess, showError } = useAlert();
  const [quotaDrawer, setQuotaDrawer] = useState(null); // { firm, direction }
  const [sendQuota, { isLoading: sendQuotaLoading }] =
    useSendDealerSubFirmQuotaMutation();

  const { data: sendableResponse, isFetching: sendableFetching, refetch: refetchSendable } =
    useGetDealerSendableSubFirmQuotaQuery(
      { id: ownFirmId, token: auth?.accessToken },
      { skip: !isCustomers || !ownFirmId || !auth?.accessToken }
    );
  const sendableQuota = useMemo(() => sendableResponse?.data || [], [sendableResponse]);

  // Bayide = bayinin kendisi (havuz + kendi cihazları); Müşterilerde = listedeki
  // müşterilerin havuz + cihaz toplamı (aramadan bağımsız değil — liste neyse o).
  const quotaCards = useMemo(() => {
    if (!isCustomers) return [];
    const customerTotals = new Map();
    scopedFirms.forEach((f) =>
      (f.quotaArray || []).forEach((q) =>
        customerTotals.set(
          q.name,
          (customerTotals.get(q.name) || 0) +
            (Number(q.firmQuota) || 0) +
            (Number(q.deviceQuota) || 0)
        )
      )
    );
    const names = sendableQuota.map((p) => p.productName);
    customerTotals.forEach((_, n) => {
      if (!names.includes(n)) names.push(n);
    });
    return names.map((productName) => ({
      productName,
      left: sendableQuota.find((p) => p.productName === productName)?.total ?? 0,
      right: customerTotals.get(productName) ?? 0,
    }));
  }, [isCustomers, scopedFirms, sendableQuota]);

  // Kota işlemi yalnız bayinin DOĞRUDAN müşterisiyle (dealerFirm'de kendisi).
  const isDirectCustomer = (firm) =>
    (firm.dealerFirm || []).some((d) => String(d?._id || d) === ownFirmId);

  const { busyIds: rawBusyIds, isBusy } = useFirmBusy(
    isCustomers ? [ownFirmId, ...scopedFirms.map((f) => f._id)] : []
  );
  const ownBusy = isBusy(ownFirmId);
  const busyIds = ownBusy
    ? new Set(scopedFirms.map((f) => String(f._id)))
    : rawBusyIds;

  const transferFirm = useMemo(
    () => (quotaDrawer?.firm ? toTransferFirm(quotaDrawer.firm) : null),
    [quotaDrawer]
  );

  const handleTransferQuota = async ({ subFirmId, productName, quota, note, direction }) => {
    try {
      const res = await sendQuota({
        token: auth.accessToken,
        id: ownFirmId,
        subFirmId,
        productName,
        quota,
        note: note || "",
        direction,
      }).unwrap();
      showSuccess(res?.message || "Kota başarıyla aktarıldı");
      setQuotaDrawer(null);
      loadFirms();
      refetchSendable();
    } catch (err) {
      showError(err?.data?.message || "Kota aktarılırken hata oluştu");
    }
  };

  const quotaBtn =
    "inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-SemiBold border transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed";
  const renderActions = (firm) => {
    if (!isDirectCustomer(firm)) return null;
    const busy = busyIds.has(String(firm._id));
    return (
      <div className="flex flex-row sm:flex-col gap-1.5 sm:items-end">
        <button
          type="button"
          disabled={busy}
          onClick={() => setQuotaDrawer({ firm, direction: "toSub" })}
          className={`${quotaBtn} bg-indigo-50 text-indigo-600 border-indigo-100 hover:bg-indigo-100`}
          title={busy ? "Firmada işlem sürüyor" : `${firm.firmName} firmasına kota ver`}
        >
          <FaPaperPlane className="w-3 h-3" /> Kota Ver
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => setQuotaDrawer({ firm, direction: "toParent" })}
          className={`${quotaBtn} bg-amber-50 text-amber-700 border-amber-100 hover:bg-amber-100`}
          title={busy ? "Firmada işlem sürüyor" : `${firm.firmName} firmasından kota al`}
        >
          <FaHandHoldingUsd className="w-3 h-3" /> Kota Al
        </button>
      </div>
    );
  };

  return (
    <PageShell>
      <FirmHeader
        isLoading={isLoading || isFetching}
        totalFirms={scopedFirms.length}
        activeCount={scopedFirms.filter((f) => f.isActive).length}
        myDealerFirm={myDealerFirm}
        title={meta.title}
        subtitle={meta.subtitle}
        icon={meta.icon}
        firmLabel={meta.firmLabel}
      />
      {isCustomers && (
        <div className="shrink-0 px-3 sm:px-4 md:px-6 pb-2 bg-gray-50/80 sm:bg-gray-50">
          <QuotaSplitCards
            items={quotaCards}
            loading={(isLoading || sendableFetching) && quotaCards.length === 0}
            leftLabel={ownBusy ? "Bayide · işlem sürüyor" : "Bayide"}
            rightLabel="Müşterilerde"
          />
        </div>
      )}
      <FirmList
        firmsData={allFirms}
        isLoading={isLoading || isFetching}
        canNavigate={canNavigateToFirm}
        variant={variant}
        searchParams={{
          firmName: firmNameSearch,
          bayserNo: bayserNoSearch,
        }}
        onSearchParamsChange={{
          setFirmName: setFirmNameSearch,
          setBayserNo: setBayserNoSearch,
        }}
        renderActions={canSendQuota ? renderActions : undefined}
        busyIds={isCustomers ? busyIds : undefined}
      />
      {canSendQuota && (
        <SubFirmQuotaDrawer
          open={!!quotaDrawer}
          onClose={() => setQuotaDrawer(null)}
          onSubmit={handleTransferQuota}
          subFirm={transferFirm}
          direction={quotaDrawer?.direction || "toSub"}
          parentFirmName={myDealerFirm?.firmName}
          sendableQuota={sendableQuota}
          subFirmQuota={toQuotaSummary(transferFirm)}
          summaryLoading={sendableFetching}
          isLoading={sendQuotaLoading}
          limitToPool
          requireLiveSource
        />
      )}
    </PageShell>
  );
}

export default DealerFirmPage;
